/**
 * Service to download firmware binary files for USB flashing.
 *
 * The firmware .bin files are served as static assets from the
 * public/firmware/ directory. The firmware record's `url` field
 * contains the relative path (e.g., /firmware/mercury-f2_0.2.0.bin).
 */

import { API_ENDPOINTS } from '../../config/config';

export class FirmwareDownloadService {

  /**
   * Downloads a firmware binary as an ArrayBuffer.
   * @param firmwareUrl - Relative URL from the firmware record (e.g., /firmware/mercury-f2_0.2.0.bin)
   * @returns The firmware binary as a Uint8Array
   * @throws Error if download fails or response is not binary
   */
  static async downloadBinary(firmwareUrl: string): Promise<Uint8Array> {
    if (!firmwareUrl) {
      throw new Error('URL de firmware no proporcionada.');
    }

    const downloadUrl = firmwareUrl.startsWith('http')
      ? firmwareUrl
      : new URL(firmwareUrl, API_ENDPOINTS.BASE_ORIGIN).toString();

    const response = await fetch(downloadUrl);

    if (!response.ok) {
      throw new Error(
        `Error al descargar firmware: ${response.status} ${response.statusText}`
      );
    }

    // Validate content type — should be binary
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('text/html') || contentType.includes('application/json')) {
      throw new Error(
        'El servidor devolvió una respuesta no binaria. Verifica que el archivo .bin existe en el servidor.'
      );
    }

    const buffer = await response.arrayBuffer();

    if (buffer.byteLength === 0) {
      throw new Error('El archivo de firmware descargado está vacío.');
    }

    return new Uint8Array(buffer);
  }
}
